<template>
  <section class="request-list">
    <header>
      <mo-pageheader>
        <template slot="title">
          {{ $t("title.dashboard") }}
        </template>
      </mo-pageheader>
    </header>
    <div>
      <dashboard-stats
        v-if="['Admin', 'Manager', 'Reader'].includes(userRole)"
      />
      <div
        class="container"
        @mousemove="onMouseMove"
        @mouseup="stopDragging"
        @mouseleave="stopDragging"
      >
        <div
          class="tile left-tile"
          :style="{ width: tileLeftWidth + '%' }"
        >
          <div class="container-title">
            <h2 class="title">
              {{ $t("page.dashboard.notifications") }}
            </h2>
          </div>
          <dashboard-filters
            tab="notification"
            @handle-filter="handleFilters"
          />
          <dashboard-list
            tab="notification"
            :cur-page="pages[DASHBOARD_TABS.NOTIFICATION]"
            :items="notifications.notifications || {}"
            :cols="notificationCols"
            @handle-page="handlePages"
          />
        </div>

        <div
          class="separator"
          @mousedown.prevent="startDragging"
        >
          <svg
            class="separator-handle"
            width="8"
            height="36"
            viewBox="0 0 8 36"
            xmlns="http://www.w3.org/2000/svg"
          >
            <g fill="#BBB">
              <circle cx="2" cy="3" r="1.5" />
              <circle cx="2" cy="9" r="1.5" />
              <circle cx="2" cy="15" r="1.5" />
              <circle cx="2" cy="21" r="1.5" />
              <circle cx="2" cy="27" r="1.5" />
              <circle cx="2" cy="33" r="1.5" />
              <circle cx="6" cy="3" r="1.5" />
              <circle cx="6" cy="9" r="1.5" />
              <circle cx="6" cy="15" r="1.5" />
              <circle cx="6" cy="21" r="1.5" />
              <circle cx="6" cy="27" r="1.5" />
              <circle cx="6" cy="33" r="1.5" />
            </g>
          </svg>
        </div>

        <div class="tile right-tile" :style="{ width: tileRightWidth + '%' }">
          <div class="container-title">
            <h2 class="title"> {{ $t("page.dashboard.alerts") }} </h2>
          </div>
          <dashboard-filters tab="alert" @handle-filter="handleFilters" />
          <dashboard-list
            tab="alert"
            :cur-page="pages[DASHBOARD_TABS.ALERT]"
            :items="notifications.notificationsAlert || {}"
            :cols="alertCols"
            @handle-page="handlePages"
          />
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { mapState } from "vuex";
import MoPageheader from "@/components/layout/nav/mo-pageheader.vue";
import DashboardFilters from "./DashboardFilters.vue";
import DashboardList from "./DashboardList.vue";
import DashboardStats from "./DashboardStats.vue";
import { DASHBOARD_TABS } from "@/constants/dashboard";

export default {
  name: "Dashboard",
  components: {
    MoPageheader,
    DashboardList,
    DashboardFilters,
    DashboardStats
  },
  data() {
    return {
      isDragging: false,
      TILE_MIN: 25,
      TILE_MAX: 75,
      tileLeftWidth: 50, // largeur en %
      tileRightWidth: 50,
      currentPage: 1,
      filters: {
        [DASHBOARD_TABS.NOTIFICATION]: {},
        [DASHBOARD_TABS.ALERT]: {}
      },
      pages: {
        [DASHBOARD_TABS.NOTIFICATION]: 1,
        [DASHBOARD_TABS.ALERT]: 1
      },
      DASHBOARD_TABS,
      notificationCols: [
        { label: "", sortable: false },
        {
          label: "Lu",
          sorted: '',
          sortable: true,
          key: "active"
        },
        {
          label: "Dossier / commande",
          sorted: '',
          sortable: true,
          key: "commande_id"
        },
        {
          label: "Date / heure",
          sorted: '',
          sortable: true,
          key: "createdAt"
        },
        {
          label: "Type",
          sorted: '',
          sortable: true,
          key: "notificationType"
        },
        {
          label: "Fournisseur / infos.",
          sorted: '',
          sortable: true,
          key: "fournisseurInfos"
        },
        { label: "Actions", sortable: false }
      ],
      alertCols: [
        { label: "", sortable: false },
        {
          label: "Lu",
          sorted: '',
          sortable: true,
          key: "active"
        },
        {
          label: "Dossier / commande",
          sorted: '',
          sortable: true,
          key: "commande_id"
        },
        {
          label: "Date alerte",
          sorted: '',
          sortable: true,
          key: "createdAt"
        },
        {
          label: "Vendeur / motif",
          sorted: '',
          sortable: true,
          key: "notificationAlertType"
        },
        { label: "Actions", sortable: false }
      ],
    };
  },
  computed: {
    ...mapState({ civilities: state => state.users.civilities }),
    ...mapState({ companies: state => state.companies.companies }),
    ...mapState({ userSubscription: state => state.users.subscription }),
    ...mapState({ notifications: state => state.notifications }),
    ...mapState({ userRole: state => state.authentication.userRole })
  },
  watch: {},
  async mounted() {
    await Promise.all([
      await this.$store.dispatch("getSellers"),
      await this.$store.dispatch("getNotificationTypes"),
      await this.$store.dispatch("getNotificationAlertTypes")
    ]);

    this.$store.commit("setSpinner", false);
  },
  methods: {
    startDragging() {
      this.isDragging = true;
    },
    stopDragging() {
      this.isDragging = false;
    },
    onMouseMove(e) {
      if (!this.isDragging) return;
      const container = this.$el;
      const totalWidth = container.clientWidth;
      const offsetX = e.clientX - container.getBoundingClientRect().left;
      let newLeftWidth = (offsetX / totalWidth) * 100;
      if (newLeftWidth < this.TILE_MIN) newLeftWidth = this.TILE_MIN;
      if (newLeftWidth > this.TILE_MAX) newLeftWidth = this.TILE_MAX;

      this.tileLeftWidth = newLeftWidth;
      this.tileRightWidth = 100 - newLeftWidth;
    },
    handleFilters(value, tab) {
      this.filters = { ...this.filters, [tab]: value };
      this.pages[tab] = 1;
      const filter = this.filters[tab] || {};

      const action =
        tab === this.DASHBOARD_TABS.NOTIFICATION
          ? "getNotifications"
          : "getNotificationsAlert";

      this.$store.dispatch(action, {
        page: 1,
        limit: Number(this.$store.getters.getLimit),
        filter
      });
    },
    handlePages(value, tab) {
      this.pages = {
        ...this.pages,
        [tab]: value
      };

      const action =
        tab === this.DASHBOARD_TABS.NOTIFICATION
          ? "getNotifications"
          : "getNotificationsAlert";

      const filter = this.filters[tab] || {};
      if (this.$route && this.$route.query && this.$route.query.orderBy) {
        const { orderBy } = this.$route.query;
        filter.orderBy = orderBy[0];
      }

      this.$store.dispatch(action, {
        page: value,
        limit: Number(this.$store.getters.getLimit),
        filter

      });
    },

  }
};
</script>

<style scoped>
.space {
  height: 2rem;
}

.tile {
  margin: 0 2rem;
  padding: 0.1rem;
  min-height: 500px;
  height: 100%;
  transition: width 0.1s ease;
}

.container {
  display: flex;
  width: 100%;
  position: relative;
  user-select: none;
}

.separator {
  width: 8px;
  cursor: col-resize;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #e5e7eb;
  z-index: 1;
}

.separator-handle {
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  gap: 2px;
}

.dot-column {
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 4px;
}

.dot {
  width: 4px;
  height: 4px;
  background-color: #d1d1d1;
  border-radius: 50%;
}

.container-title {
  padding: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
}

h2.title {
  font-size: 18px;
  text-align: center;
  font-weight: 500;
}

</style>
